import { useState, useRef, useEffect, useMemo } from 'react';
import SelectField from '../common/SelectField.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import DeliveryOtpModal from '../common/DeliveryOtpModal.js';
import shipmentQrStatusService from '../../services/shipmentQrStatusService.js';
import shipmentCrossingService from '../../services/shipmentCrossingService.js';
import { mlSheetService } from '../../services/mlSheetService.js';
import CrossingVehicleSelect from '../shipment-crossing/CrossingVehicleSelect.js';
import { prepareQrScanImageFile, isAcceptedScanFile } from '../../utils/normalizeCameraImage.js';
import { getQrBatchStatusOptions, isShipmentFullyPaid, normalizeQrResolveResponse, getQrTransitionEligibility, getEligibleShipmentsForQrStatus } from '../../utils/shipmentPayment.js';
import { isOtpRequiredResponse, normalizeOtpPending } from '../../utils/deliveryOtp.js';
import DeliveryProofCapture from '../ml-sheet/DeliveryProofCapture.js';
import { Camera, QrCode, CheckCircle, Upload, FileText, X, Shuffle, CalendarCheck, PackageOpen } from 'lucide-react';
import LiveQrScanner, { supportsLiveQrScan } from '../common/LiveQrScanner.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import { getCurrentDevicePosition } from '../../utils/deviceLocation.js';
import {
  hasShipmentCrossingAccess,
  canScanQrUpdateStatus,
  canScanQrAttendance,
  scanQrModalDefaultMode,
  isMlAccessUsersRequired,
} from '../../utils/networkMode.js';
import { parseHubAttendanceQr } from '../../utils/parseHubAttendanceQr.js';
import {
  parseShipmentIdFromQrData,
  parseShipmentUnitRefFromQrData,
  buildVerifiedUnitsByShipment,
  isShipmentUnitScanComplete,
  shipmentUnitProgressLabel,
  shipmentMissingUnitLabels,
  parcelScanAlertMessage,
} from '../../utils/parseQrShipmentId.js';
import attendanceService from '../../services/AttendanceService.js';
import { ensureCameraPermission } from '../../utils/devicePermissions.js';
import { mlSheetCoversShipments } from '../../utils/routeDisplay.js';
import { formatHubBranchLabel } from '../../utils/destinationType.js';
import {
  groupAccessAgents,
  formatAccessAgentMeta,
  matchedAccessUserIds,
} from '../../utils/mlSheetAccessAgentList.js';
import {
  ML_MOBILE_DROPDOWN_PANEL,
  ML_MOBILE_DROPDOWN_SEARCH,
  ML_MOBILE_DROPDOWN_ITEM,
  ML_MOBILE_DROPDOWN_ITEM_TEXT,
  ML_MOBILE_TRIGGER_TEXT,
  mlMobileTriggerClass,
} from '../../utils/mlSheetMobileDropdown.js';
import MlLocationChips from '../ml-sheet/MlLocationChips.js';
import MlAccessAgentChips from '../ml-sheet/MlAccessAgentChips.js';
import { toLocationsForMlDispatch } from '../../utils/hubDispatchFilter.js';

const stripUnitMergeMeta = (row) => {
  if (!row || typeof row !== 'object') return row;
  const {
    _unit_added,
    _unit_duplicate,
    _added_unit_no,
    _duplicate_unit_no,
    ...clean
  } = row;
  return clean;
};

const isPdfFile = (file) =>
  file?.type === 'application/pdf' || (file?.name || '').toLowerCase().endsWith('.pdf');

const hubOptionLabel = (hub) =>
  formatHubBranchLabel(hub)
  || hub?.location
  || hub?.hub_name
  || hub?.display_name
  || hub?.name
  || hub?.city
  || (hub?.id != null || hub?.hub_id != null ? `Hub ${hub?.id ?? hub?.hub_id}` : 'Hub');

const ScanQrUpdateStatusModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isStaff = (user?.role || user?.role_name) === 'Staff';
  const crossingAllowed = !isStaff && hasShipmentCrossingAccess(user);
  const statusAllowed = !isStaff && canScanQrUpdateStatus(user);
  const attendanceAllowed = isStaff || canScanQrAttendance(user);
  const accessUsersRequired = isMlAccessUsersRequired(user);
  const defaultMode = useMemo(() => scanQrModalDefaultMode(user), [user]);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  // 'attendance' | 'status' | 'crossing'
  const [mode, setMode] = useState(defaultMode);
  const [crossingShipments, setCrossingShipments] = useState([]);
  const [crossingFailed, setCrossingFailed] = useState([]);
  const [crossingVehicle, setCrossingVehicle] = useState(null);
  const [crossingHubCity, setCrossingHubCity] = useState(null);
  const [crossingSubmitting, setCrossingSubmitting] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [attendanceSubmitting, setAttendanceSubmitting] = useState(false);
  const [attendanceMessage, setAttendanceMessage] = useState('');

  const [scanFile, setScanFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [resolvedShipments, setResolvedShipments] = useState([]);
  const [resolveFailed, setResolveFailed] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [todayMlSheets, setTodayMlSheets] = useState([]);
  const [mlSheetsLoading, setMlSheetsLoading] = useState(false);
  const [selectedMlSheetId, setSelectedMlSheetId] = useState('');
  const [showManualMlForm, setShowManualMlForm] = useState(false);
  const [manualMlVehicle, setManualMlVehicle] = useState(null);
  const [manualMlDriverContact, setManualMlDriverContact] = useState('');
  const [manualMlFromHubIds, setManualMlFromHubIds] = useState([]);
  const [manualMlToHubIds, setManualMlToHubIds] = useState([]);
  const [manualMlAccessUserIds, setManualMlAccessUserIds] = useState([]);
  const [manualMlHubs, setManualMlHubs] = useState([]);
  const [manualMlAgents, setManualMlAgents] = useState([]);
  const [manualMlLoadingOptions, setManualMlLoadingOptions] = useState(false);
  const [creatingManualMl, setCreatingManualMl] = useState(false);
  const [manualMlOpenDropdown, setManualMlOpenDropdown] = useState(null);
  const [manualMlSearch, setManualMlSearch] = useState({ from: '', to: '', access: '' });
  const manualMlFromRef = useRef(null);
  const manualMlToRef = useRef(null);
  const manualMlAccessRef = useRef(null);
  const [deliveryReceiver, setDeliveryReceiver] = useState('');
  const [deliveryContact, setDeliveryContact] = useState('');
  const [deliveryImage, setDeliveryImage] = useState(null);
  const [deliveryImagePreview, setDeliveryImagePreview] = useState(null);
  const [deliveryPhoto, setDeliveryPhoto] = useState(null);
  const [deliveryPhotoPreview, setDeliveryPhotoPreview] = useState(null);
  const [agentUnloadingAmount, setAgentUnloadingAmount] = useState('');
  const [otpPending, setOtpPending] = useState([]);
  const [liveScannerOpen, setLiveScannerOpen] = useState(false);
  const [liveResolving, setLiveResolving] = useState(false);
  const [keepScanningOpen, setKeepScanningOpen] = useState(false);
  const liveInFlightRef = useRef(false);
  const cartRef = useRef([]);
  const crossingCartRef = useRef([]);
  useEffect(() => {
    cartRef.current = resolvedShipments;
  }, [resolvedShipments]);
  useEffect(() => {
    crossingCartRef.current = crossingShipments;
  }, [crossingShipments]);

  const canBypassLegacyQr = user?.role === 'Admin' || user?.role === 'System Admin'
    || user?.role_name === 'Admin' || user?.role_name === 'System Admin';

  const resetPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const resetManualMlForm = () => {
    setShowManualMlForm(false);
    setManualMlVehicle(null);
    setManualMlDriverContact('');
    setManualMlFromHubIds([]);
    setManualMlToHubIds([]);
    setManualMlAccessUserIds([]);
    setManualMlOpenDropdown(null);
    setManualMlSearch({ from: '', to: '', access: '' });
    setCreatingManualMl(false);
  };

  const resetScanResults = () => {
    resetPreview();
    setScanFile(null);
    setResolvedShipments([]);
    setResolveFailed([]);
    setSelectedStatus('');
    setTodayMlSheets([]);
    setSelectedMlSheetId('');
    resetManualMlForm();
    setCrossingShipments([]);
    setCrossingFailed([]);
    setCrossingVehicle(null);
    setCrossingHubCity(null);
    if (cameraRef.current) cameraRef.current.value = '';
    if (uploadRef.current) uploadRef.current.value = '';
  };

  const resetState = () => {
    resetScanResults();
    setLiveScannerOpen(false);
    setLiveResolving(false);
    liveInFlightRef.current = false;
    setMode(defaultMode);
    setRemarks('');
    setAttendanceMessage('');
    setDeliveryReceiver('');
    setDeliveryContact('');
    setDeliveryImage(null);
    setDeliveryImagePreview(null);
    setDeliveryPhoto(null);
    setDeliveryPhotoPreview(null);
    setAgentUnloadingAmount('');
    setOtpPending([]);
    setKeepScanningOpen(false);
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === mode || updating || resolving || crossingSubmitting || attendanceSubmitting) return;
    resetScanResults();
    setRemarks('');
    setAttendanceMessage('');
    setMode(nextMode);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const noticeShipments = useMemo(
    () => resolvedShipments.filter((shipment) => shipment.notice_only),
    [resolvedShipments],
  );
  const actionableShipments = useMemo(
    () => resolvedShipments.filter((shipment) => !shipment.notice_only),
    [resolvedShipments],
  );
  const statusConfig = useMemo(
    () => getQrBatchStatusOptions(actionableShipments),
    [actionableShipments],
  );
  const allowedStatuses = statusConfig.options;

  const { eligible: eligibleShipments, skipped: skippedShipments } = useMemo(
    () => getEligibleShipmentsForQrStatus(actionableShipments, selectedStatus),
    [actionableShipments, selectedStatus],
  );

  const incompleteUnitShipments = useMemo(
    () => eligibleShipments.filter((s) => !isShipmentUnitScanComplete(s)),
    [eligibleShipments],
  );
  const unitsScanIncomplete = incompleteUnitShipments.length > 0;
  const unitsScanActionLabel = selectedStatus === 'Received'
    ? 'receive'
    : selectedStatus === 'Delivered'
      ? 'deliver'
      : 'dispatch';
  // Keep short — cart already lists missing units in detail
  const unitsScanBlockMessage = unitsScanIncomplete
    ? (incompleteUnitShipments.length === 1
      ? `Scan all parcels (${shipmentUnitProgressLabel(incompleteUnitShipments[0])})`
      : `Scan all parcels (${incompleteUnitShipments.length} incomplete)`)
    : '';

  const dispatchMlLocked = Boolean(
    selectedStatus === 'Dispatched' && String(selectedMlSheetId || '').trim(),
  );

  const dispatchMlSheetGroups = useMemo(() => {
    const matching = [];
    const other = [];
    (todayMlSheets || []).forEach((sheet) => {
      if (mlSheetCoversShipments(sheet, eligibleShipments)) {
        matching.push(sheet);
      } else {
        other.push(sheet);
      }
    });
    return { matching, other };
  }, [todayMlSheets, eligibleShipments]);

  useEffect(() => {
    if (!resolvedShipments.length) {
      setSelectedStatus('');
      return;
    }
    if (statusConfig.autoSelect) {
      setSelectedStatus(statusConfig.autoSelect);
      return;
    }
    if (allowedStatuses.length > 1) {
      setSelectedStatus('');
      return;
    }
    if (allowedStatuses.length === 1) {
      setSelectedStatus(allowedStatuses[0]);
    }
  }, [resolvedShipments, statusConfig.autoSelect, allowedStatuses]);

  useEffect(() => {
    if (selectedStatus !== 'Dispatched' || !resolvedShipments.length) {
      setTodayMlSheets([]);
      setSelectedMlSheetId('');
      resetManualMlForm();
      return undefined;
    }
    let cancelled = false;
    setMlSheetsLoading(true);
    shipmentQrStatusService.listTodayMlSheets()
      .then((res) => {
        if (cancelled) return;
        const list = res?.data || [];
        setTodayMlSheets(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setTodayMlSheets([]);
      })
      .finally(() => {
        if (!cancelled) setMlSheetsLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedStatus, resolvedShipments.length]);

  const openManualMlForm = async () => {
    if (unitsScanIncomplete) {
      Alert.error(unitsScanBlockMessage || 'Scan all parcels first');
      return;
    }
    const fromIds = [
      ...new Set(
        eligibleShipments
          .map((s) => s.sender_location_hub_id)
          .filter((id) => id != null && id !== '')
          .map((id) => String(id)),
      ),
    ];
    const toIds = [
      ...new Set(
        eligibleShipments
          .map((s) => s.receiver_location_hub_id)
          .filter((id) => id != null && id !== '')
          .map((id) => String(id)),
      ),
    ];
    setShowManualMlForm(true);
    setSelectedMlSheetId('');
    setManualMlFromHubIds(fromIds);
    setManualMlToHubIds(toIds);
    setManualMlVehicle(null);
    setManualMlDriverContact('');
    setManualMlAccessUserIds([]);
    setManualMlOpenDropdown(null);
    setManualMlSearch({ from: '', to: '', access: '' });

    if (manualMlHubs.length && (!accessUsersRequired || manualMlAgents.length)) {
      return;
    }
    setManualMlLoadingOptions(true);
    try {
      const [hubsRes, agentsRes] = await Promise.all([
        mlSheetService.getLocations(),
        accessUsersRequired ? mlSheetService.getAgents() : Promise.resolve(null),
      ]);
      const hubs = hubsRes?.data?.hubs || hubsRes?.data?.data || hubsRes?.data || [];
      setManualMlHubs(Array.isArray(hubs) ? hubs : []);
      if (accessUsersRequired) {
        const agents = agentsRes?.data?.data || agentsRes?.data?.users || agentsRes?.data || [];
        setManualMlAgents(Array.isArray(agents) ? agents : []);
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Could not load hubs/agents');
    } finally {
      setManualMlLoadingOptions(false);
    }
  };

  const handleManualMlVehicleChange = (vehicle) => {
    setManualMlVehicle(vehicle);
    const contact = String(vehicle?.driver_contact || '').replace(/\D/g, '').slice(0, 10);
    if (contact) setManualMlDriverContact(contact);
  };

  const toggleManualMlHubId = (kind, hubId) => {
    const key = String(hubId);
    const setter = kind === 'from' ? setManualMlFromHubIds : setManualMlToHubIds;
    setter((prev) => (
      prev.some((id) => String(id) === key)
        ? prev.filter((id) => String(id) !== key)
        : [...prev, key]
    ));
  };

  const toggleManualMlAccessUser = (userId) => {
    const key = String(userId);
    if (key === String(user?.user_id || '')) return;
    setManualMlAccessUserIds((prev) => (
      prev.some((id) => String(id) === key)
        ? prev.filter((id) => String(id) !== key)
        : [...prev, key]
    ));
  };

  const filteredManualMlFromHubs = useMemo(() => {
    const q = (manualMlSearch.from || '').trim().toLowerCase();
    return (manualMlHubs || []).filter((hub) => {
      if (!q) return true;
      const label = hubOptionLabel(hub).toLowerCase();
      const code = String(hub.location_code || '').toLowerCase();
      return label.includes(q) || code.includes(q);
    });
  }, [manualMlHubs, manualMlSearch.from]);

  const filteredManualMlToHubs = useMemo(() => {
    const base = toLocationsForMlDispatch(manualMlHubs, manualMlFromHubIds, { enabled: true });
    const list = Array.isArray(base) && base.length ? base : manualMlHubs;
    const q = (manualMlSearch.to || '').trim().toLowerCase();
    return (list || []).filter((hub) => {
      if (!q) return true;
      const label = hubOptionLabel(hub).toLowerCase();
      const code = String(hub.location_code || '').toLowerCase();
      return label.includes(q) || code.includes(q);
    });
  }, [manualMlHubs, manualMlFromHubIds, manualMlSearch.to]);

  const manualMlAccessAgentGroups = useMemo(
    () => groupAccessAgents(
      manualMlAgents,
      manualMlSearch.access,
      manualMlToHubIds,
      manualMlHubs,
    ),
    [manualMlAgents, manualMlSearch.access, manualMlToHubIds, manualMlHubs],
  );

  useEffect(() => {
    if (!manualMlOpenDropdown) return undefined;
    const onClickOutside = (e) => {
      const refs = [manualMlFromRef, manualMlToRef, manualMlAccessRef];
      if (refs.some((ref) => ref.current?.contains(e.target))) return;
      setManualMlOpenDropdown(null);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [manualMlOpenDropdown]);

  // Auto-select destination-hub access agents when To locations change
  useEffect(() => {
    if (!showManualMlForm || !accessUsersRequired) return;
    if (!manualMlToHubIds.length || !manualMlAgents.length) return;
    const matchedIds = matchedAccessUserIds(
      manualMlAgents,
      manualMlToHubIds,
      manualMlHubs,
      user?.user_id,
    );
    if (!matchedIds.length) return;
    setManualMlAccessUserIds((prev) => {
      const seen = new Set();
      const merged = [];
      for (const id of [...matchedIds, ...(prev || [])]) {
        const key = String(id);
        if (seen.has(key) || key === String(user?.user_id || '')) continue;
        seen.add(key);
        merged.push(key);
      }
      const same =
        merged.length === (prev || []).length
        && merged.every((id, i) => String(id) === String(prev[i]));
      return same ? prev : merged;
    });
  }, [
    showManualMlForm,
    accessUsersRequired,
    manualMlToHubIds,
    manualMlAgents,
    manualMlHubs,
    user?.user_id,
  ]);

  const handleCreateManualMlAndDispatch = async () => {
    const shipmentIds = eligibleShipments.map((s) => s.shipment_id).filter(Boolean);
    if (!shipmentIds.length) {
      Alert.error('No booked shipments are eligible to dispatch');
      return;
    }
    if (unitsScanIncomplete) {
      Alert.error(unitsScanBlockMessage || 'Scan all parcels first');
      return;
    }
    const vehicleId = manualMlVehicle?.id || manualMlVehicle?.vehicle_id;
    if (!vehicleId) {
      Alert.error('Select a vehicle');
      return;
    }
    const driverContact = String(manualMlDriverContact || '').replace(/\D/g, '');
    if (driverContact.length !== 10) {
      Alert.error('Driver contact must be a 10-digit number');
      return;
    }
    if (!manualMlFromHubIds.length || !manualMlToHubIds.length) {
      Alert.error('Select from and to locations');
      return;
    }
    const fromSet = new Set(manualMlFromHubIds.map(String));
    if (manualMlToHubIds.some((id) => fromSet.has(String(id)))) {
      Alert.error('From and to locations must be different');
      return;
    }
    if (accessUsersRequired && !manualMlAccessUserIds.length) {
      Alert.error('Select at least one access agent');
      return;
    }
    if (accessUsersRequired && manualMlAccessUserIds.some((id) => String(id) === String(user?.user_id))) {
      Alert.error('You cannot assign the ML sheet to yourself. Select another access user.');
      return;
    }

    try {
      setCreatingManualMl(true);
      const payload = {
        shipment_ids: shipmentIds,
        vehicle_id: Number(vehicleId) || vehicleId,
        driver_contact: driverContact,
        from_location_ids: manualMlFromHubIds.map((id) => Number(id) || id),
        to_location_ids: manualMlToHubIds.map((id) => Number(id) || id),
        access_user_ids: accessUsersRequired ? manualMlAccessUserIds : [],
        route_id: null,
      };
      const response = await mlSheetService.createMLSheet(payload);
      const body = response?.data || response;
      const mlSheetId = body?.data?.ml_sheet_id || body?.ml_sheet_id;
      Alert.success(
        body?.message
        || (mlSheetId
          ? `Created ${mlSheetId} and dispatched ${shipmentIds.length} shipment(s)`
          : 'Manual ML created and shipments dispatched'),
      );
      resetState();
      onSuccess?.(body);
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Failed to create manual ML');
    } finally {
      setCreatingManualMl(false);
    }
  };

  const handleClose = () => {
    if (updating || resolving) return;
    resetState();
    onClose?.();
  };

  const openCamera = async () => {
    if (resolving || updating || liveResolving || attendanceSubmitting) return;
    if (supportsLiveQrScan()) {
      const allowed = await ensureCameraPermission();
      if (!allowed) {
        Alert.error('Camera permission denied. Enable camera access in app settings.');
        return;
      }
      setLiveScannerOpen(true);
      return;
    }
    cameraRef.current?.click();
  };

  const handleFileSelect = async (e, { fromCamera = false } = {}) => {
    const rawFile = e.target.files?.[0];
    if (!rawFile) return;

    if (!isAcceptedScanFile(rawFile) && !fromCamera) {
      Alert.error('Please upload a PDF or image file (PNG, JPG, GIF, WEBP)');
      return;
    }

    try {
      setResolving(true);
      const file = await prepareQrScanImageFile(rawFile, { fromCamera });
      resetPreview();
      setScanFile(file);
      if (!isPdfFile(file)) {
        setPreviewUrl(URL.createObjectURL(file));
      }
      setResolveFailed([]);
      setCrossingShipments([]);
      setCrossingFailed([]);
      await resolveScan(file);
    } catch (err) {
      console.error('Scan file prepare failed:', err);
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      if (status === 502 || status === 504 || err.code === 'ERR_NETWORK') {
        Alert.error(
          serverMsg ||
            'Upload failed (server timeout or network). Use Wi‑Fi, move closer to the QR, and try again.',
        );
      } else {
        Alert.error(
          serverMsg ||
            (fromCamera
              ? 'Could not process camera photo. Hold steady, ensure the QR is clear, and try again.'
              : 'Failed to process file. Try another image or PDF.'),
        );
      }
    } finally {
      setResolving(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  const getCurrentPosition = () => getCurrentDevicePosition();

  const markAttendanceFromQr = async (qrData) => {
    const hubId = parseHubAttendanceQr(qrData);
    if (!hubId) {
      Alert.error('This is not a location hub attendance QR. Switch tab to update status or crossing.');
      return;
    }
    setAttendanceSubmitting(true);
    try {
      Alert.info('Checking your location...');
      const coords = await getCurrentPosition();
      const response = await attendanceService.qrLoginAttendance(
        qrData,
        coords.latitude,
        coords.longitude,
      );
      const message = response.message || `Attendance marked at hub ${hubId}`;
      setAttendanceMessage(message);
      Alert.success(message);
      if (onSuccess) onSuccess(response);
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Failed to mark attendance');
    } finally {
      setAttendanceSubmitting(false);
    }
  };

  const normalizeUnitScanRow = (row) => {
    const expected = Number(row?.expected_units || row?.no_of_articles || 1) || 1;
    const units = Array.isArray(row?.units) ? row.units : [];
    const scanned = new Set();
    if (Array.isArray(row?.scanned_unit_nos)) {
      row.scanned_unit_nos.forEach((n) => {
        const num = Number(n);
        if (Number.isFinite(num) && num > 0) scanned.add(num);
      });
    }
    if (row?.scanned_unit?.unit_no != null) {
      const num = Number(row.scanned_unit.unit_no);
      if (Number.isFinite(num) && num > 0) scanned.add(num);
    }
    // Single-piece shipments: a shipment-only QR counts as unit 1.
    if (expected <= 1 && scanned.size === 0 && !row?.legacy_qr) {
      scanned.add(1);
    }
    const scanned_unit_nos = [...scanned].sort((a, b) => a - b);
    return {
      ...row,
      expected_units: expected,
      units,
      scanned_unit_nos,
      unit_scan_complete: expected <= 1 || scanned_unit_nos.length >= expected || !!row.allow_legacy_bypass,
    };
  };

  const mergeUnitIntoExisting = (existing, incoming) => {
    const existingNormalized = normalizeUnitScanRow(existing);
    const beforeSet = new Set(
      (existingNormalized.scanned_unit_nos || [])
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0),
    );
    const base = normalizeUnitScanRow({
      ...existing,
      ...incoming,
      scanned_unit_nos: existing.scanned_unit_nos || [],
      units: incoming.units?.length ? incoming.units : existing.units,
      expected_units: incoming.expected_units || existing.expected_units,
      legacy_qr: incoming.legacy_qr || existing.legacy_qr,
      allow_legacy_bypass: existing.allow_legacy_bypass || incoming.allow_legacy_bypass,
    });
    const scanned = new Set(beforeSet);
    const attemptedUnit = incoming?.scanned_unit?.unit_no != null
      ? Number(incoming.scanned_unit.unit_no)
      : null;
    if (attemptedUnit != null && Number.isFinite(attemptedUnit) && attemptedUnit > 0) {
      scanned.add(attemptedUnit);
    }
    if (Array.isArray(incoming?.scanned_unit_nos)) {
      incoming.scanned_unit_nos.forEach((n) => {
        const num = Number(n);
        if (Number.isFinite(num) && num > 0) scanned.add(num);
      });
    }
    const scanned_unit_nos = [...scanned].filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
    const newlyAdded = scanned_unit_nos.filter((n) => !beforeSet.has(n));
    const expected = Number(base.expected_units || 1) || 1;
    const addedUnitNo = newlyAdded.length === 1
      ? newlyAdded[0]
      : (newlyAdded.length > 1 ? newlyAdded[newlyAdded.length - 1] : null);
    const duplicateUnitNo = newlyAdded.length === 0
      && attemptedUnit != null
      && beforeSet.has(attemptedUnit)
      ? attemptedUnit
      : null;
    return {
      ...base,
      scanned_unit: incoming.scanned_unit || existing.scanned_unit,
      scanned_unit_nos,
      unit_scan_complete: expected <= 1 || scanned_unit_nos.length >= expected || !!base.allow_legacy_bypass,
      _unit_added: newlyAdded.length > 0,
      _unit_duplicate: duplicateUnitNo != null,
      _added_unit_no: addedUnitNo,
      _duplicate_unit_no: duplicateUnitNo,
    };
  };

  const mergeResolvedShipments = (shipments, failed = []) => {
    setResolvedShipments((prev) => {
      const map = new Map(prev.map((s) => [s.shipment_id, s]));
      shipments.forEach((incoming) => {
        const existing = map.get(incoming.shipment_id);
        if (existing) {
          map.set(incoming.shipment_id, stripUnitMergeMeta(mergeUnitIntoExisting(existing, incoming)));
        } else {
          map.set(incoming.shipment_id, stripUnitMergeMeta(normalizeUnitScanRow(incoming)));
        }
      });
      const next = Array.from(map.values());
      // Keep cartRef in sync immediately so live-scan "keep open" checks see new units.
      cartRef.current = next;
      return next;
    });
    if (failed.length) {
      setResolveFailed((prev) => {
        const map = new Map(prev.map((f) => [f.shipment_id, f]));
        failed.forEach((f) => map.set(f.shipment_id, f));
        return Array.from(map.values());
      });
    }
  };

  const removeFromCart = (shipmentId) => {
    setResolvedShipments((prev) => {
      const next = prev.filter((s) => s.shipment_id !== shipmentId);
      cartRef.current = next;
      return next;
    });
  };

  const toggleLegacyBypass = (shipmentId, enabled) => {
    setResolvedShipments((prev) => {
      const next = prev.map((row) => {
        if (row.shipment_id !== shipmentId) return row;
        return normalizeUnitScanRow({ ...row, allow_legacy_bypass: !!enabled });
      });
      cartRef.current = next;
      return next;
    });
  };

  const mergeScansIntoCart = async (shipments, failed = []) => {
    const actionable = shipments.filter((s) => !s.notice_only);
    const notices = shipments.filter((s) => s.notice_only);
    let toMerge = [...notices];
    const progressNotes = [];

    const mlSheetId = String(selectedMlSheetId || '').trim();
    const validateDispatch = selectedStatus === 'Dispatched' && mlSheetId;

    if (validateDispatch && actionable.length) {
      for (const row of actionable) {
        const sid = row.shipment_id;
        const existing = cartRef.current.find((s) => s.shipment_id === sid);
        if (existing) {
          const merged = mergeUnitIntoExisting(existing, row);
          toMerge.push(stripUnitMergeMeta(merged));
          progressNotes.push(parcelScanAlertMessage(sid, merged));
          continue;
        }
        try {
          const check = await shipmentQrStatusService.validateDispatch({
            shipment_id: sid,
            ml_sheet_id: mlSheetId,
          });
          if (check?.ok) {
            const normalized = normalizeUnitScanRow(row);
            toMerge.push(normalized);
            progressNotes.push(parcelScanAlertMessage(sid, normalized, { isNewToCart: true }));
          } else {
            Alert.error(
              check?.reason || `${sid} cannot be dispatched on this route and vehicle.`,
            );
          }
        } catch (err) {
          Alert.error(err.response?.data?.error || `${sid}: could not validate for dispatch`);
        }
      }
    } else if (actionable.length) {
      actionable.forEach((row) => {
        const existing = cartRef.current.find((s) => s.shipment_id === row.shipment_id);
        if (existing) {
          const merged = mergeUnitIntoExisting(existing, row);
          toMerge.push(stripUnitMergeMeta(merged));
          progressNotes.push(parcelScanAlertMessage(row.shipment_id, merged));
          return;
        }
        const normalized = normalizeUnitScanRow(row);
        toMerge.push(normalized);
        progressNotes.push(
          parcelScanAlertMessage(row.shipment_id, normalized, { isNewToCart: true }),
        );
      });
    }

    if (toMerge.length) {
      mergeResolvedShipments(toMerge, failed);
      if (progressNotes.length) {
        const hasDuplicate = progressNotes.some((n) => n.includes('already scanned'));
        if (hasDuplicate) {
          Alert.info(progressNotes.join(' · '));
        } else {
          Alert.success(progressNotes.join(' · '));
        }
      }
    } else if (failed.length) {
      mergeResolvedShipments([], failed);
    }
  };

  const mergeCrossingShipments = (shipments, failed = [], crossingCity) => {
    setCrossingShipments((prev) => {
      const map = new Map(prev.map((s) => [s.shipment_id, normalizeUnitScanRow(s)]));
      shipments.forEach((incoming) => {
        const existing = map.get(incoming.shipment_id);
        if (existing) {
          map.set(incoming.shipment_id, stripUnitMergeMeta(mergeUnitIntoExisting(existing, incoming)));
        } else {
          map.set(incoming.shipment_id, stripUnitMergeMeta(normalizeUnitScanRow(incoming)));
        }
      });
      const next = Array.from(map.values());
      crossingCartRef.current = next;
      return next;
    });
    if (crossingCity) setCrossingHubCity(crossingCity);
    if (failed.length) {
      setCrossingFailed((prev) => {
        const map = new Map(prev.map((f) => [f.shipment_id, f]));
        failed.forEach((f) => map.set(f.shipment_id, f));
        return Array.from(map.values());
      });
    }
  };

  const enrichShipmentsWithScannedUnit = (shipments, unitNo) => {
    if (unitNo == null || !Array.isArray(shipments)) return shipments || [];
    return shipments.map((row) => {
      if (row?.notice_only) return row;
      const existingNos = Array.isArray(row.scanned_unit_nos) ? row.scanned_unit_nos : [];
      const nos = [...new Set([...existingNos, Number(unitNo)].filter((n) => Number.isFinite(n) && n > 0))];
      const match = (row.units || []).find((u) => Number(u.unit_no) === Number(unitNo));
      return {
        ...row,
        scanned_unit: row.scanned_unit?.unit_no != null
          ? row.scanned_unit
          : {
              unit_no: Number(unitNo),
              article_type: match?.article_type || row.article_type || 'PACKAGE',
            },
        scanned_unit_nos: nos.length ? nos : [Number(unitNo)],
        legacy_qr: false,
      };
    });
  };

  const cartNeedsMoreParcelScans = () => {
    if (mode === 'crossing') {
      const rows = crossingCartRef.current || [];
      if (!rows.length) return true;
      return rows.some((row) => !row.notice_only && !isShipmentUnitScanComplete(row));
    }
    const rows = cartRef.current || [];
    if (!rows.length) return true;
    return rows.some((row) => !row.notice_only && !isShipmentUnitScanComplete(row));
  };

  const openScanMore = async () => {
    setKeepScanningOpen(true);
    await openCamera();
  };

  const resolveScan = async (file) => {
    try {
      if (mode === 'attendance') {
        Alert.error('Use live camera scan for the hub attendance QR.');
        return;
      }
      if (mode === 'crossing') {
        const response = await shipmentCrossingService.resolveFromFile(file);
        const data = response?.data || {};
        const shipments = data.shipments || [];
        if (!shipments.length) {
          throw new Error(response?.error || 'No shipment found in scan');
        }
        mergeCrossingShipments(shipments, data.failed || [], data.crossing_city || null);
        if ((data.failed || []).length > 0) {
          Alert.error(`${data.failed.length} shipment(s) could not be loaded from scan`);
        }
        return;
      }
      const response = await shipmentQrStatusService.resolveFromQr(file);
      const { shipments, failed } = normalizeQrResolveResponse(response);
      if (!shipments.length) {
        throw new Error(response?.error || 'No shipment found in scan');
      }
      await mergeScansIntoCart(shipments, failed);
      if (failed.length > 0 && !shipments.some((s) => !s.notice_only)) {
        Alert.error(`${failed.length} shipment(s) could not be loaded from scan`);
      }
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      if (status === 502 || status === 504 || err.code === 'ERR_NETWORK') {
        Alert.error(
          serverMsg ||
            'Could not reach the server. Check your network connection and try a smaller/clearer photo.',
        );
      } else {
        Alert.error(serverMsg || err.message || 'Could not read QR from file');
      }
      setResolvedShipments([]);
      setResolveFailed([]);
      setCrossingShipments([]);
      setCrossingFailed([]);
    } finally {
      setResolving(false);
    }
  };

  const handleLiveQrScan = async ({ qrData, shipmentId, imageFile, unitNo: scannedUnitNo }) => {
    if (liveInFlightRef.current || liveResolving || attendanceSubmitting) return;
    liveInFlightRef.current = true;
    try {
      setLiveResolving(true);
      const hubId = parseHubAttendanceQr(qrData);
      if (isStaff) {
        if (!hubId) {
          Alert.error('Scan the location hub attendance QR.');
          return;
        }
        await markAttendanceFromQr(qrData);
        return;
      }
      if (hubId) {
        if (!attendanceAllowed) {
          Alert.error('QR attendance is not enabled for this company.');
          return;
        }
        if (mode !== 'attendance') setMode('attendance');
        await markAttendanceFromQr(qrData);
        return;
      }

      const parsed = parseShipmentUnitRefFromQrData(qrData);
      const resolvedShipmentId = shipmentId || parsed.shipmentId || parseShipmentIdFromQrData(qrData);
      const unitNo = scannedUnitNo ?? parsed.unitNo;
      // Keep camera open while multi-parcel shipments still need units.
      setKeepScanningOpen(true);
      let targetMode = mode;
      if (mode === 'attendance') {
        targetMode = statusAllowed ? 'status' : (crossingAllowed ? 'crossing' : '');
        if (!targetMode) {
          Alert.error('This is not a location hub attendance QR. Switch tab to update status or crossing.');
          return;
        }
        setMode(targetMode);
      }

      const applyCrossing = (response) => {
        const data = response?.data || response || {};
        const shipments = enrichShipmentsWithScannedUnit(data.shipments || [], unitNo);
        if (!shipments.length) {
          throw new Error(response?.error || data?.error || 'Shipment not found or access denied');
        }
        const progressParts = [];
        shipments.filter((s) => !s.notice_only).forEach((row) => {
          const existing = crossingCartRef.current.find((s) => s.shipment_id === row.shipment_id);
          if (existing) {
            const merged = mergeUnitIntoExisting(existing, row);
            progressParts.push(parcelScanAlertMessage(row.shipment_id, merged));
          } else {
            progressParts.push(
              parcelScanAlertMessage(row.shipment_id, normalizeUnitScanRow(row), { isNewToCart: true }),
            );
          }
        });
        mergeCrossingShipments(shipments, data.failed || [], data.crossing_city);
        if (progressParts.length) {
          const hasDuplicate = progressParts.some((n) => n.includes('already scanned'));
          if (hasDuplicate) Alert.info(progressParts.join(' · '));
          else Alert.success(progressParts.join(' · '));
        }
        if ((data.failed || []).length > 0) {
          Alert.error(`${data.failed.length} shipment(s) could not be loaded`);
        }
      };

      const applyStatus = async (response) => {
        const normalized = normalizeQrResolveResponse(response);
        const shipments = enrichShipmentsWithScannedUnit(normalized.shipments, unitNo);
        const failed = normalized.failed || [];
        if (!shipments.length) {
          throw new Error(response?.error || 'Shipment not found or access denied');
        }
        await mergeScansIntoCart(shipments, failed);
        if (failed.length > 0 && !shipments.some((s) => !s.notice_only)) {
          Alert.error(`${failed.length} shipment(s) could not be loaded`);
        }
      };

      if (targetMode === 'crossing') {
        try {
          const response = await shipmentCrossingService.resolveFromData({
            qrData,
            shipmentId: resolvedShipmentId,
            unitNo,
          });
          applyCrossing(response);
        } catch (err) {
          if (!imageFile) throw err;
          const response = await shipmentCrossingService.resolveFromFile(imageFile);
          applyCrossing(response);
        }
        return;
      }

      try {
        const response = await shipmentQrStatusService.resolveFromQrData({
          qrData,
          shipmentId: resolvedShipmentId,
          unitNo,
        });
        await applyStatus(response);
      } catch (err) {
        if (!imageFile) throw err;
        const response = await shipmentQrStatusService.resolveFromQr(imageFile);
        await applyStatus(response);
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Could not resolve scanned QR');
    } finally {
      liveInFlightRef.current = false;
      setLiveResolving(false);
      const keepOpen = cartNeedsMoreParcelScans()
        && (mode === 'status' || mode === 'crossing');
      if (keepOpen) {
        setKeepScanningOpen(true);
        setLiveScannerOpen(true);
      } else {
        setLiveScannerOpen(false);
        setKeepScanningOpen(false);
      }
    }
  };

  const handleDispatch = async (mlSheetId) => {
    if (!mlSheetId || updating) return;
    if (unitsScanIncomplete) {
      Alert.error(unitsScanBlockMessage || 'Scan all parcels first');
      return;
    }
    const shipmentIds = eligibleShipments.map((s) => s.shipment_id);
    if (!shipmentIds.length) {
      Alert.error('No booked shipments are eligible to dispatch');
      return;
    }
    try {
      setUpdating(true);
      setSelectedMlSheetId(mlSheetId);
      const response = await shipmentQrStatusService.dispatchOntoMlSheet({
        shipmentIds,
        mlSheetId,
        verified_units_by_shipment: buildVerifiedUnitsByShipment(eligibleShipments),
        allow_legacy_qr: eligibleShipments.some((s) => s.allow_legacy_bypass),
      });
      if (response?.failed_count > 0) {
        const detail = (response.failed || [])
          .map((f) => `${f.shipment_id}: ${f.reason}`)
          .join('; ');
        Alert.error(response.message || detail || 'Some shipments could not be dispatched');
        if (response.shipment_ids?.length) {
          Alert.success(`Dispatched: ${response.shipment_ids.join(', ')}`);
          setResolvedShipments((prev) => {
            const next = prev.filter(
              (s) => !(response.shipment_ids || []).includes(s.shipment_id),
            );
            cartRef.current = next;
            return next;
          });
        }
        if (onSuccess) onSuccess(response);
        return;
      }
      Alert.success(response.message || 'Shipment dispatched');
      resetState();
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setSelectedMlSheetId(mlSheetId);
      Alert.error(err.response?.data?.error || err.message || 'Failed to dispatch');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdate = async () => {
    if (!resolvedShipments.length) {
      Alert.error('Scan a shipment QR first');
      return;
    }
    if (!selectedStatus) {
      Alert.error('Select a status');
      return;
    }
    if (selectedStatus === 'Dispatched') {
      if (!selectedMlSheetId) {
        Alert.error('Select an ML sheet');
        return;
      }
      await handleDispatch(selectedMlSheetId);
      return;
    }
    if (!allowedStatuses.includes(selectedStatus)) {
      Alert.error('Selected status is not allowed for these shipments');
      return;
    }
    if (selectedStatus === 'Delivered') {
      const unpaid = eligibleShipments
        .filter((s) => !isShipmentFullyPaid(s))
        .map((s) => s.shipment_id);
      if (unpaid.length > 0) {
        Alert.error(
          `Cannot mark as Delivered until payment is complete. Unpaid: ${unpaid.join(', ')}`,
        );
        return;
      }
      if (!deliveryReceiver.trim()) {
        Alert.error('Receiver name is required');
        return;
      }
      if (!deliveryContact.trim()) {
        Alert.error('Receiver contact number is required');
        return;
      }
      if (!deliveryImage) {
        Alert.error('Government ID proof image is required');
        return;
      }
      if (!deliveryPhoto) {
        Alert.error('Signed GC receipt is required');
        return;
      }
    }

    if (eligibleShipments.length === 0) {
      Alert.error('No shipments are eligible for this status update');
      return;
    }

    if (unitsScanIncomplete) {
      Alert.error(unitsScanBlockMessage || `Scan all parcels to ${unitsScanActionLabel}`);
      return;
    }

    try {
      setUpdating(true);
      const shipmentIds = eligibleShipments.map((s) => s.shipment_id);
      const payload = {
        shipment_ids: shipmentIds,
        status: selectedStatus,
        verified_units_by_shipment: buildVerifiedUnitsByShipment(eligibleShipments),
        allow_legacy_qr: eligibleShipments.some((s) => s.allow_legacy_bypass) || undefined,
      };
      if (remarks.trim()) payload.remarks = remarks.trim();
      if (selectedStatus === 'Delivered') {
        if (deliveryReceiver.trim()) payload.delivery_receiver = deliveryReceiver.trim();
        if (deliveryContact.trim()) payload.delivery_receiver_contact = deliveryContact.trim();
        if (agentUnloadingAmount !== '') payload.agent_unloading_amount = agentUnloadingAmount;
      }

      const response = await shipmentQrStatusService.updateStatusFromQr(payload, {
        delivery_receiver_image: deliveryImage,
        delivery_photo: deliveryPhoto,
      });

      if (isOtpRequiredResponse(response)) {
        setOtpPending(normalizeOtpPending(response));
        if (response.updated_count > 0) {
          Alert.success(response.message || 'OTP sent. Verify to complete remaining deliveries.');
        } else {
          Alert.success(response.message || 'OTP sent on WhatsApp');
        }
        return;
      }

      if (response.failed_count > 0) {
        const failedIds = (response.failed || []).map((f) => f.shipment_id).join(', ');
        Alert.error(response.message || `Some updates failed: ${failedIds}`);
        const successIds = new Set(response.shipment_ids || response.updated_ids || []);
        if (successIds.size) {
          setResolvedShipments((prev) => {
            const next = prev.filter((s) => !successIds.has(s.shipment_id));
            cartRef.current = next;
            return next;
          });
        }
      } else if (response.skipped_count > 0 && response.updated_count > 0) {
        Alert.success(response.message || 'Status updated successfully');
      } else if (response.skipped_count > 0 && !response.updated_count) {
        Alert.success(response.message || 'No changes needed');
      } else {
        Alert.success(response.message || 'Status updated successfully');
      }

      if (!(response.failed_count > 0)) {
        onSuccess?.(response);
        resetState();
        onClose?.();
      } else {
        onSuccess?.(response);
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const unloadEligibleCrossing = crossingShipments.filter(
    (s) => s.can_unload && isShipmentUnitScanComplete(s),
  );
  const dispatchEligibleCrossing = crossingShipments.filter(
    (s) => s.can_dispatch && isShipmentUnitScanComplete(s),
  );

  const refreshCrossingShipments = async (shipmentIds) => {
    const ids = (shipmentIds || []).filter(Boolean);
    if (!ids.length) return;
    const response = await shipmentCrossingService.resolveFromData({
      shipmentIds: ids,
      action: 'dispatch',
    });
    const data = response?.data || {};
    const shipments = (data.shipments || []).map((row) => {
      const prev = crossingShipments.find((s) => s.shipment_id === row.shipment_id);
      if (!prev) return normalizeUnitScanRow(row);
      return normalizeUnitScanRow({
        ...row,
        scanned_unit_nos: prev.scanned_unit_nos,
        scanned_unit: prev.scanned_unit,
        allow_legacy_bypass: prev.allow_legacy_bypass,
        units: row.units?.length ? row.units : prev.units,
        expected_units: row.expected_units || prev.expected_units,
      });
    });
    if (shipments.length) {
      setCrossingShipments(shipments);
      setCrossingFailed(data.failed || []);
      if (data.crossing_city) setCrossingHubCity(data.crossing_city);
    }
  };

  const handleUnloadCrossing = async () => {
    if (!crossingShipments.length) {
      Alert.error('Scan a shipment QR first');
      return;
    }
    const incompleteUnload = crossingShipments.filter(
      (s) => s.can_unload && !isShipmentUnitScanComplete(s),
    );
    if (incompleteUnload.length) {
      Alert.error(
        incompleteUnload.length === 1
          ? `Scan all parcels (${shipmentUnitProgressLabel(incompleteUnload[0])})`
          : `Scan all parcels (${incompleteUnload.length} incomplete)`,
      );
      return;
    }
    if (unloadEligibleCrossing.length === 0) {
      Alert.error('No scanned shipments are ready to unload. Unload only works for Dispatched shipments at this hub.');
      return;
    }

    const unloadIds = unloadEligibleCrossing.map((s) => s.shipment_id);
    const allScannedIds = crossingShipments.map((s) => s.shipment_id);

    try {
      setCrossingSubmitting(true);
      const response = await shipmentCrossingService.unloadShipments({
        shipmentIds: unloadIds,
        remarks: remarks.trim() || undefined,
        verified_units_by_shipment: buildVerifiedUnitsByShipment(unloadEligibleCrossing),
        allow_legacy_qr: unloadEligibleCrossing.some((s) => s.allow_legacy_bypass),
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some unloads failed');
      } else {
        Alert.success(
          `${response.message || 'Shipments unloaded at crossing hub'}. Select a vehicle below to dispatch again.`,
        );
      }

      onSuccess?.(response);
      setRemarks('');
      // Stay open: refresh scanned shipments so Dispatch again appears without re-scan.
      await refreshCrossingShipments(allScannedIds);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to unload at crossing');
    } finally {
      setCrossingSubmitting(false);
    }
  };

  const handleCreateCrossing = async () => {
    if (!crossingShipments.length) {
      Alert.error('Scan a shipment QR first');
      return;
    }
    if (!crossingVehicle) {
      Alert.error('Select a vehicle for dispatch');
      return;
    }
    const incompleteRedispatch = crossingShipments.filter(
      (s) => s.can_dispatch && !isShipmentUnitScanComplete(s),
    );
    if (incompleteRedispatch.length) {
      Alert.error(
        incompleteRedispatch.length === 1
          ? `Scan all parcels (${shipmentUnitProgressLabel(incompleteRedispatch[0])})`
          : `Scan all parcels (${incompleteRedispatch.length} incomplete)`,
      );
      return;
    }
    if (dispatchEligibleCrossing.length === 0) {
      Alert.error('Unload the shipment first, then dispatch again onto a new vehicle');
      return;
    }

    try {
      setCrossingSubmitting(true);
      const response = await shipmentCrossingService.createCrossing({
        shipmentIds: dispatchEligibleCrossing.map((s) => s.shipment_id),
        newVehicleId: crossingVehicle.id,
        remarks: remarks.trim() || undefined,
        verified_units_by_shipment: buildVerifiedUnitsByShipment(dispatchEligibleCrossing),
        allow_legacy_qr: dispatchEligibleCrossing.some((s) => s.allow_legacy_bypass),
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some dispatches failed');
      } else {
        Alert.success(response.message || 'Dispatched after crossing successfully');
      }

      onSuccess?.(response);
      resetState();
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to dispatch after crossing');
    } finally {
      setCrossingSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (isStaff) {
    return (
      <LiveQrScanner
        open
        onClose={handleClose}
        onScan={handleLiveQrScan}
        busy={liveResolving || attendanceSubmitting}
        continuous={false}
        title="Scan Attendance QR"
        hint="Hold the printed hub QR in the frame. Stay within 150m of the hub."
        failureMessage="Could not open the camera. Allow camera access, then tap Scan QR again."
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={otpPending.length ? undefined : handleClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scan-qr-modal-title"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <h2 id="scan-qr-modal-title" className="text-lg font-bold text-gray-800 flex items-center gap-2">
              {mode === 'crossing' ? (
                <Shuffle className="w-5 h-5 text-orange-500" />
              ) : mode === 'attendance' ? (
                <CalendarCheck className="w-5 h-5 text-orange-500" />
              ) : (
                <QrCode className="w-5 h-5 text-orange-500" />
              )}
              {mode === 'crossing'
                ? 'Scan QR — Crossing'
                : mode === 'attendance'
                  ? 'Scan QR — Attendance'
                  : 'Scan QR — Update Status'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {mode === 'attendance'
                ? 'Open camera, scan the printed hub QR, and keep GPS on.'
                : mode === 'crossing'
                  ? 'Unload Dispatched shipments first, then dispatch Received shipments onto a new vehicle.'
                  : 'Open camera to auto-detect QR, or upload an image/PDF.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={updating || resolving || crossingSubmitting || attendanceSubmitting}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {[attendanceAllowed, statusAllowed, crossingAllowed].filter(Boolean).length > 1 && (
          <div className="px-4 sm:px-6 pt-3">
            <div className={`grid gap-1 p-1 bg-gray-100 rounded-lg ${
              [attendanceAllowed, statusAllowed, crossingAllowed].filter(Boolean).length === 3
                ? 'grid-cols-3'
                : 'grid-cols-2'
            }`}>
              {attendanceAllowed && (
                <button
                  type="button"
                  onClick={() => handleModeChange('attendance')}
                  className={`py-2 text-sm font-medium rounded-md transition-colors ${
                    mode === 'attendance' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Attendance
                </button>
              )}
              {statusAllowed && (
                <button
                  type="button"
                  onClick={() => handleModeChange('status')}
                  className={`py-2 text-sm font-medium rounded-md transition-colors ${
                    mode === 'status' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Update Status
                </button>
              )}
              {crossingAllowed && (
                <button
                  type="button"
                  onClick={() => handleModeChange('crossing')}
                  className={`py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                    mode === 'crossing' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  <Shuffle className="w-4 h-4" /> Crossing
                </button>
              )}
            </div>
          </div>
        )}

        <div className="p-4 sm:p-6 space-y-4">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFileSelect(e, { fromCamera: true })}
          />
          <input
            ref={uploadRef}
            type="file"
            accept=".pdf,image/png,image/jpeg,image/jpg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp,application/pdf"
            className="hidden"
            onChange={(e) => handleFileSelect(e, { fromCamera: false })}
          />

          {mode === 'attendance' && (
            <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-3">
              Stand at the location hub, scan the printed attendance QR, and keep GPS on.
              You must be within 150 metres of the hub.
            </p>
          )}

          <div className={`grid gap-2 ${mode === 'attendance' ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <Button
              variant="primary"
              icon={Camera}
              fullWidth
              onClick={openCamera}
              disabled={resolving || updating || liveResolving || attendanceSubmitting}
            >
              {attendanceSubmitting ? 'Marking attendance…' : liveResolving ? 'Reading QR…' : 'Open Camera'}
            </Button>
            {mode !== 'attendance' && (
            <Button
              variant="secondary"
              icon={Upload}
              fullWidth
              onClick={() => uploadRef.current?.click()}
              disabled={resolving || updating}
            >
              Upload
            </Button>
            )}
          </div>

          {attendanceMessage && mode === 'attendance' && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
              {attendanceMessage}
            </p>
          )}

          {scanFile && (
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
              {isPdfFile(scanFile) ? (
                <div className="flex items-center gap-3">
                  <FileText className="w-10 h-10 text-red-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{scanFile.name}</p>
                    <p className="text-xs text-gray-500">PDF — QR read from document pages</p>
                  </div>
                </div>
              ) : previewUrl ? (
                <img src={previewUrl} alt="QR scan preview" className="w-full max-h-40 object-contain rounded" />
              ) : null}
            </div>
          )}

          {resolving && (
            <div className="py-4">
              <Loader fullScreen={false} size="small" message="Reading QR from file..." />
            </div>
          )}

          {mode === 'status' && resolvedShipments.length > 0 && !resolving && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-semibold text-gray-800">
                  Cart · {actionableShipments.length} shipment{actionableShipments.length === 1 ? '' : 's'}
                </p>
                {actionableShipments.length > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    icon={QrCode}
                    className="!py-1.5 !text-xs"
                    onClick={openScanMore}
                    disabled={resolving || updating || liveResolving}
                  >
                    Scan more
                  </Button>
                )}
              </div>

              {dispatchMlLocked && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  ML sheet locked. Further scans must belong to this route or they will not be added.
                </p>
              )}

              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {resolvedShipments.map((shipment) => {
                  const eligibility = selectedStatus
                    ? getQrTransitionEligibility(shipment, selectedStatus)
                    : null;
                  const willUpdate = eligibility?.eligible;
                  const willSkip = eligibility && !eligibility.eligible;

                  return (
                  <div
                    key={shipment.shipment_id}
                    className={`p-3 text-sm ${willUpdate ? 'bg-green-50/80' : willSkip ? 'bg-gray-50' : 'bg-orange-50/50'}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <strong>{shipment.shipment_id}</strong>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-800 bg-white border border-gray-200 rounded px-1.5 py-0.5">
                          {shipmentUnitProgressLabel(shipment)} pcs
                        </span>
                        <span className="capitalize text-gray-700">{shipment.status}</span>
                        {!shipment.notice_only && (
                          <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            onClick={() => removeFromCart(shipment.shipment_id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-600 mt-1">
                      {shipment.sender_location} → {shipment.receiver_location}
                    </p>
                    {shipment.customer_details_visible !== false && (shipment.sender_name || shipment.receiver_name) ? (
                      <p className="text-xs text-gray-700 mt-1">
                        {[shipment.sender_name, shipment.receiver_name].filter(Boolean).join(' → ')}
                      </p>
                    ) : null}
                    {shipment.scanned_unit?.article_type ? (
                      <p className="text-xs text-gray-700 mt-1">
                        Last scan: {shipment.scanned_unit.article_type}
                        {shipment.scanned_unit.unit_no != null
                          ? ` · unit ${shipment.scanned_unit.unit_no}`
                          : ''}
                      </p>
                    ) : null}
                    {!isShipmentUnitScanComplete(shipment) && !shipment.notice_only ? (
                      <p className="text-xs text-amber-800 mt-1">
                        Missing: {shipmentMissingUnitLabels(shipment).join(', ') || 'scan remaining parcel QRs'}
                      </p>
                    ) : null}
                    {(shipment.legacy_qr || shipment.requires_unit_scan) && (shipment.expected_units || 1) > 1 && canBypassLegacyQr ? (
                      <label className="mt-1 flex items-center gap-2 text-xs text-amber-900">
                        <input
                          type="checkbox"
                          checked={!!shipment.allow_legacy_bypass}
                          onChange={(e) => toggleLegacyBypass(shipment.shipment_id, e.target.checked)}
                        />
                        Admin legacy bypass (old sticker without unit)
                      </label>
                    ) : null}
                    {(shipment.legacy_qr || shipment.requires_unit_scan) && (shipment.expected_units || 1) > 1 && !canBypassLegacyQr ? (
                      <p className="text-xs text-amber-800 mt-1">
                        {shipment.legacy_qr_message || 'Reprint parcel stickers — this QR has no unit code.'}
                      </p>
                    ) : null}
                    <p className="mt-1">
                      <span className="text-gray-500">Payment:</span>{' '}
                      {shipment.amounts_visible === false ? (
                        <span className="text-amber-800 font-medium">
                          {String(shipment.mode_of_payment || '').toLowerCase() === 'to_pay'
                            ? 'To-Pay · settle ML to unlock'
                            : 'Hidden until settled'}
                        </span>
                      ) : (
                        <span className={isShipmentFullyPaid(shipment) ? 'text-green-700 font-medium' : 'text-red-600 font-medium'}>
                          {isShipmentFullyPaid(shipment)
                            ? 'Paid'
                            : `₹${Number(shipment.balance_amount || 0).toFixed(2)} due`}
                        </span>
                      )}
                    </p>
                    {shipment.delivery_locked_until_settlement && (
                      <p className="text-xs text-amber-800 font-semibold mt-1">
                        {String(shipment.mode_of_payment || '').toLowerCase() === 'to_pay'
                          ? `Settle ${shipment.ml_sheet_id || 'this ML sheet'} before receiving parcel. To-Pay details stay hidden until then.`
                          : `Settle ${shipment.ml_sheet_id || 'this ML sheet'} before delivery.`}
                      </p>
                    )}
                    {shipment.notice_only && (
                      <p className="text-xs text-amber-800 mt-1">{shipment.notice_message}</p>
                    )}
                    {selectedStatus && willSkip && !shipment.notice_only && (
                      <p className="text-xs text-amber-700 mt-1">{eligibility.reason}</p>
                    )}
                    {selectedStatus && willUpdate && (
                      <p className="text-xs text-green-700 mt-1">
                        {eligibility?.reapply
                          ? `Will confirm ${selectedStatus} again`
                          : `Will update to ${selectedStatus}`}
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>

              {selectedStatus && skippedShipments.length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  {skippedShipments.length} shipment(s) will be skipped for {selectedStatus}.
                  {eligibleShipments.length > 0
                    ? ` ${eligibleShipments.length} will be updated.`
                    : ' None eligible to update.'}
                </p>
              )}

              {resolveFailed.length > 0 && (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  <p className="font-medium">Skipped from scan:</p>
                  {resolveFailed.map((item) => (
                    <p key={item.shipment_id}>
                      {item.shipment_id}: {item.reason}
                    </p>
                  ))}
                </div>
              )}

              {statusConfig.paymentMessage && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {statusConfig.paymentMessage}
                </p>
              )}

              {noticeShipments.length > 0 && actionableShipments.length === 0 ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {noticeShipments[0].notice_message || 'This scan was sent to the destination branch and admin. Status was not changed.'}
                </p>
              ) : allowedStatuses.length === 0 ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  This shipment cannot be updated to Received or Delivered from its current status.
                </p>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Status <span className="text-red-500">*</span>
                    </label>
                    <SelectField variant="form"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                    >
                      <option value="">Select status...</option>
                      {allowedStatuses.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </SelectField>
                    <p className="text-xs text-gray-500 mt-1">
                      Booked → Dispatched. Dispatched → Received. Received → confirm again or Delivered (if paid).
                    </p>
                  </div>

                  {selectedStatus === 'Dispatched' && (
                    <div className="space-y-3">
                      {showManualMlForm ? (
                        <div className="space-y-3 border border-orange-200 bg-orange-50/60 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-gray-800">Create manual ML</p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                Mandatory fields only. Creates the ML sheet and dispatches the cart shipments.
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={resetManualMlForm}
                              disabled={creatingManualMl}
                            >
                              Cancel
                            </Button>
                          </div>

                          {manualMlLoadingOptions ? (
                            <p className="text-sm text-gray-500">Loading locations…</p>
                          ) : (
                            <>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="relative min-w-0" ref={manualMlFromRef}>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    From locations <span className="text-red-500">*</span>
                                  </label>
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      if (creatingManualMl) return;
                                      setManualMlOpenDropdown((prev) => (prev === 'from' ? null : 'from'));
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        if (!creatingManualMl) {
                                          setManualMlOpenDropdown((prev) => (prev === 'from' ? null : 'from'));
                                        }
                                      }
                                    }}
                                    className={mlMobileTriggerClass(manualMlOpenDropdown === 'from')}
                                  >
                                    <span className={ML_MOBILE_TRIGGER_TEXT}>
                                      {manualMlFromHubIds.length > 0
                                        ? `${manualMlFromHubIds.length} selected`
                                        : 'Select locations'}
                                    </span>
                                    <span className="shrink-0 text-gray-500">▾</span>
                                  </div>
                                  {manualMlOpenDropdown === 'from' && (
                                    <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                      <input
                                        type="text"
                                        placeholder="Search locations..."
                                        value={manualMlSearch.from}
                                        onChange={(e) => setManualMlSearch((prev) => ({
                                          ...prev,
                                          from: e.target.value,
                                        }))}
                                        autoFocus
                                        className={ML_MOBILE_DROPDOWN_SEARCH}
                                      />
                                      <div className="max-h-48 overflow-y-auto">
                                        {filteredManualMlFromHubs.length > 0 ? (
                                          filteredManualMlFromHubs.map((hub) => {
                                            const id = hub.id ?? hub.hub_id;
                                            return (
                                              <label key={`from-${id}`} className={ML_MOBILE_DROPDOWN_ITEM}>
                                                <input
                                                  type="checkbox"
                                                  checked={manualMlFromHubIds.some((v) => String(v) === String(id))}
                                                  onChange={() => toggleManualMlHubId('from', id)}
                                                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                                                />
                                                <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>
                                                  {hubOptionLabel(hub)}
                                                </span>
                                              </label>
                                            );
                                          })
                                        ) : (
                                          <p className="text-sm text-gray-500 p-2">No locations found</p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  <div className="mt-1 min-w-0">
                                    <MlLocationChips
                                      compact
                                      locations={manualMlHubs}
                                      selectedIds={manualMlFromHubIds}
                                      onRemove={creatingManualMl ? undefined : (id) => toggleManualMlHubId('from', id)}
                                    />
                                  </div>
                                </div>

                                <div className="relative min-w-0" ref={manualMlToRef}>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    To locations <span className="text-red-500">*</span>
                                  </label>
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      if (creatingManualMl) return;
                                      setManualMlOpenDropdown((prev) => (prev === 'to' ? null : 'to'));
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        if (!creatingManualMl) {
                                          setManualMlOpenDropdown((prev) => (prev === 'to' ? null : 'to'));
                                        }
                                      }
                                    }}
                                    className={mlMobileTriggerClass(manualMlOpenDropdown === 'to')}
                                  >
                                    <span className={ML_MOBILE_TRIGGER_TEXT}>
                                      {manualMlToHubIds.length > 0
                                        ? `${manualMlToHubIds.length} selected`
                                        : 'Select locations'}
                                    </span>
                                    <span className="shrink-0 text-gray-500">▾</span>
                                  </div>
                                  {manualMlOpenDropdown === 'to' && (
                                    <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                      <input
                                        type="text"
                                        placeholder="Search locations..."
                                        value={manualMlSearch.to}
                                        onChange={(e) => setManualMlSearch((prev) => ({
                                          ...prev,
                                          to: e.target.value,
                                        }))}
                                        autoFocus
                                        className={ML_MOBILE_DROPDOWN_SEARCH}
                                      />
                                      <div className="max-h-48 overflow-y-auto">
                                        {filteredManualMlToHubs.length > 0 ? (
                                          filteredManualMlToHubs.map((hub) => {
                                            const id = hub.id ?? hub.hub_id;
                                            return (
                                              <label key={`to-${id}`} className={ML_MOBILE_DROPDOWN_ITEM}>
                                                <input
                                                  type="checkbox"
                                                  checked={manualMlToHubIds.some((v) => String(v) === String(id))}
                                                  onChange={() => toggleManualMlHubId('to', id)}
                                                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                                                />
                                                <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>
                                                  {hubOptionLabel(hub)}
                                                </span>
                                              </label>
                                            );
                                          })
                                        ) : (
                                          <p className="text-sm text-gray-500 p-2">
                                            {manualMlFromHubIds.length === 0
                                              ? 'Select From location first'
                                              : 'No destination hubs found'}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  <div className="mt-1 min-w-0">
                                    <MlLocationChips
                                      compact
                                      locations={manualMlHubs}
                                      selectedIds={manualMlToHubIds}
                                      onRemove={creatingManualMl ? undefined : (id) => toggleManualMlHubId('to', id)}
                                    />
                                  </div>
                                </div>
                              </div>

                              <CrossingVehicleSelect
                                value={manualMlVehicle}
                                onChange={handleManualMlVehicleChange}
                                disabled={creatingManualMl}
                              />

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Driver contact <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="tel"
                                  inputMode="numeric"
                                  value={manualMlDriverContact}
                                  onChange={(e) => setManualMlDriverContact(
                                    String(e.target.value || '').replace(/\D/g, '').slice(0, 10),
                                  )}
                                  placeholder="10-digit mobile"
                                  maxLength={10}
                                  disabled={creatingManualMl}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm bg-white"
                                />
                                {manualMlDriverContact.length > 0 && manualMlDriverContact.length !== 10 && (
                                  <p className="text-xs text-red-600 mt-1">Must be exactly 10 digits</p>
                                )}
                              </div>

                              {unitsScanIncomplete && (
                                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5">
                                  {unitsScanBlockMessage}
                                </p>
                              )}

                              {accessUsersRequired && (
                                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                                  <div className="relative w-full sm:w-56 lg:w-64 shrink-0" ref={manualMlAccessRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Access agents <span className="text-red-500">*</span>
                                    </label>
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => {
                                        if (creatingManualMl) return;
                                        setManualMlOpenDropdown((prev) => (prev === 'access' ? null : 'access'));
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          if (!creatingManualMl) {
                                            setManualMlOpenDropdown((prev) => (prev === 'access' ? null : 'access'));
                                          }
                                        }
                                      }}
                                      className={mlMobileTriggerClass(manualMlOpenDropdown === 'access')}
                                    >
                                      <span className={ML_MOBILE_TRIGGER_TEXT}>
                                        {manualMlAccessUserIds.length > 0
                                          ? `${manualMlAccessUserIds.length} selected`
                                          : 'Select users'}
                                      </span>
                                      <span className="shrink-0 text-gray-500">▾</span>
                                    </div>
                                    {manualMlOpenDropdown === 'access' && (
                                      <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                        <input
                                          type="text"
                                          placeholder="Search by name, location, contact..."
                                          value={manualMlSearch.access}
                                          onChange={(e) => setManualMlSearch((prev) => ({
                                            ...prev,
                                            access: e.target.value,
                                          }))}
                                          autoFocus
                                          className={ML_MOBILE_DROPDOWN_SEARCH}
                                        />
                                        <div className="max-h-56 overflow-y-auto">
                                          {manualMlAccessAgentGroups.all.length > 0 ? (
                                            <>
                                              {manualMlAccessAgentGroups.hasToFilter
                                                && manualMlAccessAgentGroups.matched.length > 0 && (
                                                <>
                                                  <p className="text-[11px] font-semibold text-indigo-700 px-2 py-1 sticky top-0 bg-white">
                                                    Agents at destination
                                                  </p>
                                                  {manualMlAccessAgentGroups.matched.map((agent) => {
                                                    const agentId = agent.user_id || agent.id;
                                                    const meta = formatAccessAgentMeta(agent);
                                                    return (
                                                      <label key={agentId} className={ML_MOBILE_DROPDOWN_ITEM}>
                                                        <input
                                                          type="checkbox"
                                                          checked={manualMlAccessUserIds.some(
                                                            (id) => String(id) === String(agentId),
                                                          )}
                                                          onChange={() => toggleManualMlAccessUser(agentId)}
                                                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0"
                                                        />
                                                        <div className="min-w-0">
                                                          <p className={`${ML_MOBILE_DROPDOWN_ITEM_TEXT} font-semibold text-secondary`}>
                                                            {agent.name || agent.full_name || agentId}
                                                          </p>
                                                          {meta ? (
                                                            <p className="text-[9px] sm:text-xs text-gray-500 mt-0.5 break-words">
                                                              {meta}
                                                            </p>
                                                          ) : null}
                                                        </div>
                                                      </label>
                                                    );
                                                  })}
                                                </>
                                              )}
                                              {manualMlAccessAgentGroups.hasToFilter
                                                && manualMlAccessAgentGroups.others.length > 0 && (
                                                <>
                                                  <p className="text-[11px] font-semibold text-gray-500 px-2 py-1 mt-1 border-t border-gray-100 sticky top-0 bg-white">
                                                    Other agents
                                                  </p>
                                                  {manualMlAccessAgentGroups.others.map((agent) => {
                                                    const agentId = agent.user_id || agent.id;
                                                    const meta = formatAccessAgentMeta(agent);
                                                    return (
                                                      <label key={agentId} className={ML_MOBILE_DROPDOWN_ITEM}>
                                                        <input
                                                          type="checkbox"
                                                          checked={manualMlAccessUserIds.some(
                                                            (id) => String(id) === String(agentId),
                                                          )}
                                                          onChange={() => toggleManualMlAccessUser(agentId)}
                                                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0"
                                                        />
                                                        <div className="min-w-0">
                                                          <p className={`${ML_MOBILE_DROPDOWN_ITEM_TEXT} font-semibold text-secondary`}>
                                                            {agent.name || agent.full_name || agentId}
                                                          </p>
                                                          {meta ? (
                                                            <p className="text-[9px] sm:text-xs text-gray-500 mt-0.5 break-words">
                                                              {meta}
                                                            </p>
                                                          ) : null}
                                                        </div>
                                                      </label>
                                                    );
                                                  })}
                                                </>
                                              )}
                                              {!manualMlAccessAgentGroups.hasToFilter
                                                && manualMlAccessAgentGroups.all.map((agent) => {
                                                  const agentId = agent.user_id || agent.id;
                                                  const meta = formatAccessAgentMeta(agent);
                                                  return (
                                                    <label key={agentId} className={ML_MOBILE_DROPDOWN_ITEM}>
                                                      <input
                                                        type="checkbox"
                                                        checked={manualMlAccessUserIds.some(
                                                          (id) => String(id) === String(agentId),
                                                        )}
                                                        onChange={() => toggleManualMlAccessUser(agentId)}
                                                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0"
                                                      />
                                                      <div className="min-w-0">
                                                        <p className={`${ML_MOBILE_DROPDOWN_ITEM_TEXT} font-semibold text-secondary`}>
                                                          {agent.name || agent.full_name || agentId}
                                                        </p>
                                                        {meta ? (
                                                          <p className="text-[9px] sm:text-xs text-gray-500 mt-0.5 break-words">
                                                            {meta}
                                                          </p>
                                                        ) : null}
                                                      </div>
                                                    </label>
                                                  );
                                                })}
                                            </>
                                          ) : (
                                            <p className="text-sm text-gray-500 p-2">No users available</p>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1 sm:pt-6">
                                    <MlAccessAgentChips
                                      agents={manualMlAgents}
                                      selectedIds={manualMlAccessUserIds}
                                      onRemove={creatingManualMl ? undefined : toggleManualMlAccessUser}
                                    />
                                  </div>
                                </div>
                              )}

                              <Button
                                variant="primary"
                                icon={CheckCircle}
                                fullWidth
                                onClick={handleCreateManualMlAndDispatch}
                                loading={creatingManualMl}
                                disabled={
                                  creatingManualMl
                                  || eligibleShipments.length === 0
                                  || unitsScanIncomplete
                                }
                              >
                                {`Create ML & Dispatch${eligibleShipments.length > 0 ? ` (${eligibleShipments.length})` : ''}`}
                              </Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              ML Sheet <span className="text-red-500">*</span>
                            </label>
                            {mlSheetsLoading ? (
                              <p className="text-sm text-gray-500">Loading today’s ML sheets…</p>
                            ) : todayMlSheets.length === 0 ? (
                              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 space-y-2">
                                <p>No open ML today.</p>
                                {unitsScanIncomplete && (
                                  <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5">
                                    {unitsScanBlockMessage}
                                  </p>
                                )}
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={openManualMlForm}
                                  disabled={unitsScanIncomplete}
                                >
                                  Create manual ML
                                </Button>
                              </div>
                            ) : (
                              <>
                                {dispatchMlSheetGroups.matching.length === 0 && (
                                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mb-2">
                                    No matching ML today — pick one or create manual.
                                  </p>
                                )}
                                {unitsScanIncomplete && (
                                  <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 mb-2">
                                    {unitsScanBlockMessage}
                                  </p>
                                )}
                                <SelectField
                                  variant="form"
                                  value={selectedMlSheetId}
                                  onChange={(e) => setSelectedMlSheetId(e.target.value)}
                                  disabled={updating || unitsScanIncomplete}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                                >
                                  <option value="">Select ML sheet...</option>
                                  {dispatchMlSheetGroups.matching.map((sheet) => {
                                    const route = [sheet.from_locations, sheet.to_locations]
                                      .map((names) => (names || []).filter(Boolean).join(', '))
                                      .filter(Boolean)
                                      .join(' → ');
                                    const label = [sheet.ml_sheet_id, sheet.vehicle_number, route].filter(Boolean).join(' — ');
                                    return (
                                      <option key={sheet.ml_sheet_id} value={sheet.ml_sheet_id}>
                                        {label} (route match)
                                      </option>
                                    );
                                  })}
                                  {dispatchMlSheetGroups.other.length > 0 && (
                                    <option value="" disabled>
                                      — Other routes —
                                    </option>
                                  )}
                                  {dispatchMlSheetGroups.other.map((sheet) => {
                                    const route = [sheet.from_locations, sheet.to_locations]
                                      .map((names) => (names || []).filter(Boolean).join(', '))
                                      .filter(Boolean)
                                      .join(' → ');
                                    const label = [sheet.ml_sheet_id, sheet.vehicle_number, route].filter(Boolean).join(' — ');
                                    return (
                                      <option key={`other-${sheet.ml_sheet_id}`} value={sheet.ml_sheet_id}>
                                        {label}
                                      </option>
                                    );
                                  })}
                                </SelectField>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={openManualMlForm}
                                    disabled={unitsScanIncomplete}
                                  >
                                    New manual ML (Custom)
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                          {(dispatchMlLocked || unitsScanIncomplete) && (
                            <Button
                              type="button"
                              variant="secondary"
                              icon={QrCode}
                              fullWidth
                              onClick={openScanMore}
                              disabled={resolving || updating || liveResolving}
                            >
                              Scan more parcels
                            </Button>
                          )}
                          <Button
                            variant="primary"
                            icon={CheckCircle}
                            fullWidth
                            onClick={() => handleDispatch(selectedMlSheetId)}
                            loading={updating}
                            disabled={
                              updating
                              || !selectedMlSheetId
                              || eligibleShipments.length === 0
                              || unitsScanIncomplete
                            }
                          >
                            Dispatch
                            {eligibleShipments.length > 0 ? ` (${eligibleShipments.length})` : ''}
                          </Button>
                        </>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      rows={2}
                      placeholder="Add remarks for this status update..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm resize-none"
                    />
                  </div>

                  {selectedStatus === 'Delivered' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-orange-50 rounded-xl border border-orange-200">
                      <div className="sm:col-span-2">
                        <h4 className="text-sm font-semibold text-orange-800">Delivery Information</h4>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Receiver Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={deliveryReceiver}
                          onChange={(e) => setDeliveryReceiver(e.target.value)}
                          placeholder="Person who received the delivery"
                          maxLength={250}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Receiver Contact <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={deliveryContact}
                          onChange={(e) => setDeliveryContact(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="10-digit contact number"
                          inputMode="numeric"
                          maxLength={10}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        />
                      </div>
                      <DeliveryProofCapture
                        label="Government ID proof"
                        required
                        file={deliveryImage}
                        preview={deliveryImagePreview}
                        inputId="qr-delivery-id"
                        onError={(msg) => Alert.error(msg)}
                        onFile={(next) => {
                          setDeliveryImage(next)
                          if (!next) {
                            setDeliveryImagePreview(null)
                            return
                          }
                          const reader = new FileReader()
                          reader.onloadend = () => setDeliveryImagePreview(reader.result)
                          reader.readAsDataURL(next)
                        }}
                      />
                      <DeliveryProofCapture
                        label="Signed GC receipt"
                        hint="Upload the printed receipt PDF, or a photo of the signed short receipt. GC number must be visible."
                        required
                        allowPdf
                        file={deliveryPhoto}
                        preview={deliveryPhotoPreview}
                        inputId="qr-delivery-photo"
                        onError={(msg) => Alert.error(msg)}
                        onFile={(next) => {
                          setDeliveryPhoto(next)
                          if (!next) {
                            setDeliveryPhotoPreview(null)
                            return
                          }
                          const reader = new FileReader()
                          reader.onloadend = () => setDeliveryPhotoPreview(reader.result)
                          reader.readAsDataURL(next)
                        }}
                      />
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Unloading amount (agent only)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={agentUnloadingAmount}
                          onChange={(e) => setAgentUnloadingAmount(e.target.value)}
                          placeholder="Not added to GC or ML totals"
                          className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {selectedStatus !== 'Dispatched' && (
                    <div className="space-y-3">
                      {unitsScanIncomplete && (
                        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5">
                          {unitsScanBlockMessage}
                        </p>
                      )}
                      {unitsScanIncomplete && (
                        <Button
                          type="button"
                          variant="secondary"
                          icon={QrCode}
                          fullWidth
                          onClick={openScanMore}
                          disabled={resolving || updating || liveResolving}
                        >
                          Scan more parcels
                        </Button>
                      )}
                      <Button
                        variant="primary"
                        icon={CheckCircle}
                        fullWidth
                        onClick={handleUpdate}
                        loading={updating}
                        disabled={
                          updating
                          || !selectedStatus
                          || eligibleShipments.length === 0
                          || unitsScanIncomplete
                        }
                      >
                        {selectedStatus === 'Received'
                          ? 'Receive'
                          : selectedStatus === 'Delivered'
                            ? 'Deliver'
                            : 'Update Status'}
                        {selectedStatus && eligibleShipments.length > 0
                          ? ` (${eligibleShipments.length})`
                          : ''}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {mode === 'crossing' && crossingShipments.length > 0 && !resolving && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-semibold text-gray-800">
                  {crossingShipments.length > 1
                    ? `${crossingShipments.length} shipment(s) found`
                    : 'Shipment details'}
                </p>
                <div className="flex items-center gap-2">
                  {crossingHubCity && (
                    <span className="text-xs text-gray-500">Crossing at: {crossingHubCity}</span>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    icon={QrCode}
                    className="!py-1.5 !text-xs"
                    onClick={openScanMore}
                    disabled={resolving || crossingSubmitting || liveResolving}
                  >
                    Scan more
                  </Button>
                </div>
              </div>

              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {crossingShipments.map((shipment) => {
                  const ready =
                    (shipment.can_unload || shipment.can_dispatch) && isShipmentUnitScanComplete(shipment);
                  const stepHint = shipment.can_unload
                    ? (isShipmentUnitScanComplete(shipment) ? 'Ready to unload' : `Missing parcels (${shipmentUnitProgressLabel(shipment)})`)
                    : shipment.can_dispatch
                      ? (isShipmentUnitScanComplete(shipment) ? 'Ready to dispatch again' : `Missing parcels (${shipmentUnitProgressLabel(shipment)})`)
                      : shipment.next_action === 'unload'
                        ? shipment.unload_reason
                        : shipment.dispatch_reason || shipment.reason;
                  return (
                    <div
                      key={shipment.shipment_id}
                      className={`p-3 text-sm ${ready ? 'bg-green-50/80' : 'bg-gray-50'}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <strong>{shipment.shipment_id}</strong>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-800 bg-white border border-gray-200 rounded px-1.5 py-0.5">
                            {shipmentUnitProgressLabel(shipment)} pcs
                          </span>
                          <span className="capitalize text-gray-700">{shipment.status}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 mt-1">
                        {shipment.sender_location} → {shipment.receiver_location}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Current vehicle: {shipment.current_vehicle_number || 'N/A'}
                        {shipment.ml_sheet_id ? ` · ML: ${shipment.ml_sheet_id}` : ''}
                      </p>
                      {!isShipmentUnitScanComplete(shipment) && !shipment.notice_only ? (
                        <p className="text-xs text-amber-800 mt-1">
                          Missing: {shipmentMissingUnitLabels(shipment).join(', ') || 'scan remaining parcel QRs'}
                        </p>
                      ) : null}
                      {(shipment.legacy_qr || shipment.requires_unit_scan) && (shipment.expected_units || 1) > 1 && canBypassLegacyQr ? (
                        <label className="mt-1 flex items-center gap-2 text-xs text-amber-900">
                          <input
                            type="checkbox"
                            checked={!!shipment.allow_legacy_bypass}
                            onChange={(e) => {
                              const enabled = e.target.checked;
                              setCrossingShipments((prev) => prev.map((row) => (
                                row.shipment_id === shipment.shipment_id
                                  ? normalizeUnitScanRow({ ...row, allow_legacy_bypass: enabled })
                                  : row
                              )));
                            }}
                          />
                          Admin legacy bypass (old sticker without unit)
                        </label>
                      ) : null}
                      {stepHint && (
                        <p className={`text-xs mt-1 ${ready ? 'text-green-700' : 'text-amber-700'}`}>
                          {stepHint}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {crossingFailed.length > 0 && (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  <p className="font-medium">Skipped from scan:</p>
                  {crossingFailed.map((item) => (
                    <p key={item.shipment_id}>
                      {item.shipment_id}: {item.reason}
                    </p>
                  ))}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={2}
                  placeholder="Add a note for unload or dispatch..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm resize-none"
                />
              </div>

              <div className="space-y-3 rounded-lg border border-gray-200 p-3 bg-gray-50/60">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  1. Unload at hub
                </p>
                {unloadEligibleCrossing.length > 0 ? (
                  <Button
                    variant="primary"
                    icon={PackageOpen}
                    fullWidth
                    onClick={handleUnloadCrossing}
                    loading={crossingSubmitting}
                    disabled={crossingSubmitting}
                  >
                    Unload at hub
                    {` (${unloadEligibleCrossing.length})`}
                  </Button>
                ) : (
                  <p className="text-xs text-gray-500">
                    {dispatchEligibleCrossing.length > 0
                      ? 'Already unloaded. Continue with dispatch below.'
                      : 'No Dispatched shipments ready to unload from this scan.'}
                  </p>
                )}
              </div>

              <div className="space-y-3 rounded-lg border border-gray-200 p-3 bg-gray-50/60">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  2. Dispatch again
                </p>
                {dispatchEligibleCrossing.length > 0 ? (
                  <>
                    <CrossingVehicleSelect
                      value={crossingVehicle}
                      onChange={setCrossingVehicle}
                      disabled={crossingSubmitting}
                    />
                    <Button
                      variant="primary"
                      icon={Shuffle}
                      fullWidth
                      onClick={handleCreateCrossing}
                      loading={crossingSubmitting}
                      disabled={crossingSubmitting || !crossingVehicle}
                    >
                      Dispatch again
                      {` (${dispatchEligibleCrossing.length})`}
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">
                    {unloadEligibleCrossing.length > 0
                      ? 'Unload first — then vehicle select and dispatch appear here without scanning again.'
                      : 'No Received shipments ready to dispatch from this scan.'}
                  </p>
                )}
              </div>

              {unloadEligibleCrossing.length === 0 && dispatchEligibleCrossing.length === 0 && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  No scanned shipments are ready for unload or dispatch at this hub.
                </p>
              )}

              <p className="text-xs text-gray-400">
                Crossing is operational only: unload then change the carrying vehicle.
                The ML sheet, on contract, and commission stay unchanged.
              </p>
            </div>
          )}
        </div>
      </div>

      <LiveQrScanner
        open={liveScannerOpen}
        onClose={() => {
          setLiveScannerOpen(false);
          setKeepScanningOpen(false);
        }}
        onScan={handleLiveQrScan}
        busy={liveResolving || attendanceSubmitting}
        continuous={mode === 'status' || mode === 'crossing'}
        title={mode === 'attendance' ? 'Scan Attendance QR' : 'Scan Shipment QR'}
        hint={
          mode === 'attendance'
            ? 'Hold the printed hub QR in the frame. Stay within 150m of the hub.'
            : 'Scan every parcel sticker. Camera stays open until all pieces are scanned.'
        }
      />
      <DeliveryOtpModal
        open={otpPending.length > 0}
        pending={otpPending}
        onClose={() => setOtpPending([])}
        onVerified={() => {
          setOtpPending([]);
          Alert.success('Delivery verified and completed');
          onSuccess?.({ success: true, otp_verified: true });
          resetState();
          onClose?.();
        }}
      />
    </div>
  );
};

export default ScanQrUpdateStatusModal;
